import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VisibilityIcon from "@mui/icons-material/Visibility";
import classNames from "classnames";
import { BandBadge, TablePager } from "../../primitives";
import { BlockBaseProps } from "../../types";
import styles from "../table.module.scss";
import PlanHistoryTableSkeleton from "./Skeleton";

export interface PlanHistoryRow {
  id: string | number;
  name: string;
  duration: string;
  /** Already formatted, without the currency prefix. */
  price: string;
  /** Pass "—" when the plan carried no discount. */
  discounted: string;
  /**
   * How to colour the discount: "none" for "—", "saved" for a real reduction,
   * and the default plain ink for the plan currently running.
   */
  discountTone?: "none" | "saved";
  status: string;
  start: string;
  end: string;
}

interface PlanHistoryTableProps extends BlockBaseProps {
  plans: PlanHistoryRow[];
  onViewPlan?: (id: string | number) => void;
  onViewReceipt?: (id: string | number) => void;
  title?: string;
  currency?: string;
  /** Marks the plan whose payments are currently shown below. */
  selectedId?: string | number;
  /** Server-side paging. Omit `total` to render the table without a pager. */
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  emptyLabel?: string;
}

const COLUMNS = [
  "Plan",
  "Duration",
  "Plan price",
  "Discounted price",
  "Plan status",
  "Start date",
  "End date",
  "Actions",
];

/** Every plan the school has held, with the active one tinted. */
function PlanHistoryTable({
  plans,
  onViewPlan,
  onViewReceipt,
  title = "Plan history",
  currency = "Rs.",
  selectedId,
  page = 0,
  pageSize = 10,
  total,
  onPageChange,
  emptyLabel = "This school has never held a plan.",
  loading,
  className,
}: PlanHistoryTableProps) {
  if (loading) {
    return (
      <PlanHistoryTableSkeleton
        count={plans.length || 3}
        className={className}
      />
    );
  }

  return (
    <div className={classNames(styles.card, className)}>
      <div className={styles.heading}>{title}</div>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column} className={styles.th}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!plans.length && (
              <tr>
                <td className={`${styles.td} ${styles.empty}`} colSpan={COLUMNS.length}>
                  {emptyLabel}
                </td>
              </tr>
            )}
            {plans.map((plan) => (
              <tr
                key={plan.id}
                className={
                  plan.status === "Active" || plan.id === selectedId
                    ? styles.rowActive
                    : undefined
                }
              >
                <td className={`${styles.td} ${styles.strong}`}>{plan.name}</td>
                <td className={`${styles.td} ${styles.nowrap}`}>{plan.duration}</td>
                <td className={`${styles.td} ${styles.nowrap}`}>
                  {`${currency} ${plan.price}`}
                </td>
                <td
                  className={classNames(
                    styles.td,
                    styles.nowrap,
                    styles.bold,
                    plan.discountTone === "none" && styles.discountNone,
                    plan.discountTone === "saved" && styles.discountSaved
                  )}
                >
                  {plan.discounted}
                </td>
                <td className={styles.td}>
                  <BandBadge band={plan.status} status />
                </td>
                <td className={`${styles.td} ${styles.nowrap}`}>{plan.start}</td>
                <td className={`${styles.td} ${styles.nowrap}`}>{plan.end}</td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      aria-label="View plan details"
                      className={styles.iconBtn}
                      onClick={() => onViewPlan?.(plan.id)}
                    >
                      <VisibilityIcon />
                    </button>
                    <button
                      type="button"
                      aria-label="View invoice"
                      className={styles.iconBtn}
                      onClick={() => onViewReceipt?.(plan.id)}
                    >
                      <ReceiptLongIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total != null && total > pageSize && onPageChange && (
        <TablePager
          page={page}
          pageSize={pageSize}
          total={total}
          onChange={onPageChange}
        />
      )}
    </div>
  );
}

export default PlanHistoryTable;
